import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = async (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (event?.detail?.data?.didError) return;

    let ready = this.#drawerHasLineItems();

    if (!ready) {
      if (event?.detail?.data?.source === 'product-form-component') {
        ready = await this.#waitForDrawerLineItems();
      } else {
        ready = await this.#ensureDrawerContent();
      }
    }

    if (!this.#drawerHasLineItems()) return;

    this.showDialog();
    this.#updateStickyState();
  };

  /**
   * Waits for product-form's in-flight drawer refresh after auto-open.
   * @param {number} [maxMs]
   * @returns {Promise<boolean>}
   */
  async #waitForDrawerLineItems(maxMs = 3000) {
    const start = performance.now();

    while (performance.now() - start < maxMs) {
      if (this.#drawerHasLineItems()) return true;
      await new Promise((resolve) => {
        window.setTimeout(resolve, 50);
      });
    }

    return this.#ensureDrawerContent();
  };

  #handleCartUpdate = () => {
    requestAnimationFrame(() => {
      this.#updateStickyState();
    });
  };

  /**
   * @returns {Promise<object | null>}
   */
  async #fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then((response) => response.json())
      .catch(() => null);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDrawerCartItems() {
    return this.querySelector('cart-items-component[data-drawer]');
  }

  /**
   * @returns {boolean}
   */
  #drawerHasLineItems() {
    return Boolean(this.#getDrawerCartItems()?.querySelector('.cart-form'));
  }

  /**
   * @returns {Promise<boolean>} Whether the drawer has line items ready to display.
   */
  async #ensureDrawerContent() {
    await customElements.whenDefined('cart-items-component');

    const cart = await this.#fetchCart();

    if (!cart?.item_count) return false;

    const drawerCartItems = this.#getDrawerCartItems();
    if (!(drawerCartItems instanceof HTMLElement)) return false;

    if (this.#drawerHasLineItems()) {
      /** @type {any} */ (drawerCartItems).syncCartDrawerEmptyState?.();
      return true;
    }

    if (typeof /** @type {any} */ (drawerCartItems).refreshDrawer !== 'function') {
      if (Shopify.designMode) {
        console.warn('[cart-drawer] refreshDrawer is unavailable on cart-items-component');
      }
      return false;
    }

    let refreshed = await /** @type {any} */ (drawerCartItems).refreshDrawer(undefined, {
      fromAddToCart: true,
    });

    if (!refreshed) {
      await new Promise((resolve) => window.setTimeout(resolve, 200));
      refreshed = await /** @type {any} */ (drawerCartItems).refreshDrawer(undefined, {
        fromAddToCart: true,
      });
    }

    return refreshed;
  }

  async open() {
    const cart = await this.#fetchCart();
    let ready = await this.#ensureDrawerContent();

    if (cart?.item_count && !ready) {
      ready = await this.#ensureDrawerContent();
    }

    if (cart?.item_count && !ready) return;

    this.showDialog();
    this.#updateStickyState();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
